import type { AcknowledgmentSummary, AckUser } from '@goodchat/shared/types';

interface AcknowledgmentBarProps {
  summary: AcknowledgmentSummary;
  onAcknowledge: () => void;
}

function AvatarStack({ users, total, label }: { users: AckUser[]; total: number; label: string }) {
  if (users.length === 0 && total === 0) return null;
  const remaining = total - users.length;

  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] text-gray-600 dark:text-gray-300">{label}:</span>
      <div className="flex -space-x-1.5">
        {users.map((u) => (
          <div
            key={u.userId}
            className="flex h-5 w-5 items-center justify-center rounded-full border border-white bg-gray-200 text-[8px] font-medium text-gray-600 dark:border-gray-800 dark:bg-gray-700 dark:text-gray-300"
            title={u.displayName}
          >
            {u.profilePictureUrl ? (
              <img src={u.profilePictureUrl} alt="" className="h-5 w-5 rounded-full object-cover" />
            ) : (
              u.displayName.charAt(0).toUpperCase()
            )}
          </div>
        ))}
      </div>
      {remaining > 0 && (
        <span className="text-[10px] text-gray-400">+{remaining} more</span>
      )}
    </div>
  );
}

export function AcknowledgmentBar({ summary, onAcknowledge }: AcknowledgmentBarProps) {
  const percent = summary.total > 0 ? (summary.acknowledged / summary.total) * 100 : 0;

  return (
    <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 dark:border-amber-800 dark:bg-amber-900/40">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center justify-between text-xs">
            <span className="font-medium text-amber-700 dark:text-amber-400">
              {summary.acknowledged} of {summary.total} acknowledged
            </span>
            <span className="text-amber-600 dark:text-amber-500">{Math.round(percent)}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-amber-200 dark:bg-amber-900">
            <div
              className="h-full rounded-full bg-amber-500 transition-all dark:bg-amber-400"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>
        {summary.userAcknowledged ? (
          <div className="flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            Done
          </div>
        ) : (
          <button
            onClick={onAcknowledge}
            className="rounded-md bg-amber-600 px-3.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-amber-700"
          >
            Acknowledge
          </button>
        )}
      </div>

      {/* Social proof: who acknowledged / who's waiting */}
      {(summary.acknowledgedUsers?.length || summary.pendingUsers?.length) ? (
        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
          {summary.acknowledgedUsers && summary.acknowledgedUsers.length > 0 && (
            <AvatarStack users={summary.acknowledgedUsers} total={summary.acknowledged} label="Done" />
          )}
          {summary.pendingUsers && summary.pendingUsers.length > 0 && (
            <AvatarStack users={summary.pendingUsers} total={summary.pending} label="Waiting" />
          )}
        </div>
      ) : null}
    </div>
  );
}
